/**
 * ValueStream360 — ENTERPRISE360's value-stream diagnostic (the front door).
 * Distinct from the 12 *functional* lenses: here a visitor picks the end-to-end
 * VALUE STREAM they want to assess (they don't have to take on the whole
 * enterprise), gets a 3-minute maturity read, and is led into the full
 * Reinvention Blueprint. Built on the existing `ek-` design tokens.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Layers, Workflow, ServerCog, CheckCircle2 } from "lucide-react";
import { liteRoute } from "../data/family360";
import { API_BASE } from "../lib/api";
import { getAllSnapshots } from "../lib/maturitySnapshots";
import PortfolioShareModal from "../components/portfolioRadar/PortfolioShareModal";

const LEVEL_BAND = { 1: "Initial", 2: "Defined", 3: "Managed", 4: "Optimised" };

export default function ValueStream360() {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assessed, setAssessed] = useState({}); // slug -> { overall }
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    let active = true;
    fetch(`${API_BASE}/public/valuestream360/streams`)
      .then((r) => r.json())
      .then((d) => { if (active) { setStreams(d.streams || []); setLoading(false); } })
      .catch(() => active && setLoading(false));
    // Read the visitor's completed value-stream assessments from their device history.
    const snaps = getAllSnapshots() || {};
    const vs = {};
    Object.keys(snaps).forEach((slug) => { if (slug.startsWith("vs-")) vs[slug] = snaps[slug]; });
    setAssessed(vs);
    return () => { active = false; };
  }, []);

  const business = streams.filter((s) => !s.is_enabling_layer);
  const enabling = streams.filter((s) => s.is_enabling_layer);
  const assessedCount = Object.keys(assessed).length;

  const Card = ({ s }) => {
    const snap = assessed[s.slug];
    const band = snap ? LEVEL_BAND[Math.round(snap.overall)] : null;
    return (
    <div className={`ek-card flex flex-col ${snap ? "ring-1 ring-[#7737F6]/40" : ""}`} data-testid={`vs-card-${s.slug}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 flex items-center justify-center bg-[#F2F2F3] text-[#7737F6]">
          {s.is_enabling_layer ? <ServerCog size={20} strokeWidth={1.5} /> : <Workflow size={20} strokeWidth={1.5} />}
        </div>
        {snap && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#7737F6] bg-[#7737F6]/8 border border-[#7737F6]/20 rounded-full px-2.5 py-0.5" data-testid={`vs-assessed-${s.slug}`}>
            <CheckCircle2 size={12} /> {snap.overall}/4 · {band}
          </span>
        )}
      </div>
      <h3 className="ek-heading text-xl text-black leading-snug">{s.name}</h3>
      <p className="ek-body text-[14px] mt-2 flex-1 line-clamp-3">{s.purpose}</p>
      {s.specialist_lenses?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-4">
          {s.specialist_lenses.map((l) => (
            <span key={l} className="text-[10px] font-medium tracking-[0.1em] text-[#7737F6] bg-[#7737F6]/8 border border-[#7737F6]/20 rounded-full px-2.5 py-0.5">{l}</span>
          ))}
        </div>
      )}
      <Link to={liteRoute(s.slug)} className="mt-5 inline-flex items-center gap-1.5 text-[#7737F6] text-[13px] font-medium hover:underline border-t border-zinc-200 pt-4" data-testid={`vs-start-${s.slug}`}>
        <Sparkles size={13} /> {snap ? "Re-assess this value stream" : "Assess this value stream — 3 min"}
      </Link>
    </div>
    );
  };

  return (
    <div data-testid="valuestream360-page">
      {/* Hero */}
      <section className="relative ek-gradient-dark text-white overflow-hidden" data-testid="vs-hero">
        <div className="ek-star-watermark" />
        <div className="ek-vignette-top" />
        <div className="ek-container relative z-10 py-20 md:py-28">
          <div className="ek-eyebrow text-[#A179FA] ek-fade-up">ValueStream360 · the ENTERPRISE360 diagnostic</div>
          <h1 className="ek-heading text-5xl md:text-6xl lg:text-7xl text-white mt-5 max-w-4xl ek-fade-up" style={{ animationDelay: "60ms" }} data-testid="vs-headline">
            Pick a value stream. <span className="text-[#A179FA]">See how it really runs.</span>
          </h1>
          <p className="text-zinc-300 font-light text-lg md:text-xl leading-relaxed mt-7 max-w-2xl ek-fade-up" style={{ animationDelay: "120ms" }}>
            The twelve EKSELENS360 lenses assess <em>functions</em>. ENTERPRISE360 assesses the way a
            business actually runs — end-to-end <strong>value streams</strong>. Choose the one that's
            on your mind, answer a few questions, and get an honest maturity read in three minutes.
            Start with one; build up to the whole enterprise when you're ready.
          </p>
          <div className="flex flex-wrap gap-4 mt-9 ek-fade-up" style={{ animationDelay: "180ms" }}>
            <Link to="/enterprise360" className="ek-btn ek-btn-primary" data-testid="vs-engine-cta">
              How the engine works <ArrowRight size={16} />
            </Link>
            <Link to="/the-360-family" className="ek-btn ek-btn-outline-light" data-testid="vs-family-cta">
              See the full 360 family
            </Link>
          </div>
        </div>
      </section>

      {/* Progress strip — appears once the visitor has assessed at least one stream */}
      {assessedCount > 0 && (
        <section className="bg-[#0B0B0C] text-white border-b border-white/10" data-testid="vs-progress-strip">
          <div className="ek-container py-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="text-[#A179FA]">
                <CheckCircle2 size={22} />
              </div>
              <div>
                <div className="text-[15px] font-medium" data-testid="vs-progress-count">
                  {assessedCount} of {streams.length || 12} value streams assessed
                </div>
                <div className="text-zinc-400 text-[13px]">
                  Assess 2–3 and we can assemble a focused Reinvention Blueprint across just those streams.
                </div>
              </div>
            </div>
            <button onClick={() => setShareOpen(true)} className="ek-btn ek-btn-primary shrink-0" data-testid="vs-progress-cta">
              Send my maturity profile to Ekselens <ArrowRight size={16} />
            </button>
          </div>
        </section>
      )}

      <PortfolioShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        snapshots={assessed}
        completedCount={assessedCount}
        context={{
          eyebrow: "ValueStream360 · Share your read",
          title: `Send your ${assessedCount}-value-stream maturity read to Ekselens.`,
          blurb: "We'll come back within one business day with a founder-led, sector-calibrated perspective on the value streams you've assessed — and how a focused Reinvention Blueprint would tackle the gaps. No slide pitch, no obligation.",
          consentLabel: "I'd like Ekselens to follow up about my value-stream maturity. I understand my scores and contact details will be stored to enable this conversation.",
          submitLabel: "Send my read",
          counterLabel: `Sharing ${assessedCount} value-stream score${assessedCount === 1 ? "" : "s"}`,
          successBlurb: "A one-page branded PDF of your value-stream read is being emailed to you now. A founder-led conversation will follow within one business day with a focused next step.",
        }}
      />

      {/* Business value streams */}
      <section className="bg-white ek-section" data-testid="vs-business-section">
        <div className="ek-container">
          <div className="ek-eyebrow">Core value streams</div>
          <h2 className="ek-heading text-4xl md:text-5xl mt-4 text-black mb-12">How the business creates value.</h2>
          {loading ? (
            <p className="ek-body text-zinc-500" data-testid="vs-loading">Loading value streams…</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ek-stagger">
              {business.map((s) => <Card key={s.slug} s={s} />)}
            </div>
          )}
        </div>
      </section>

      {/* Enabling layers */}
      {enabling.length > 0 && (
        <section className="bg-[#F2F2F3] ek-section" data-testid="vs-enabling-section">
          <div className="ek-container">
            <div className="flex items-center gap-2 ek-eyebrow"><Layers size={14} /> Enabling layers</div>
            <h2 className="ek-heading text-4xl md:text-5xl mt-4 text-black mb-12">What lets the business run.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ek-stagger">
              {enabling.map((s) => <Card key={s.slug} s={s} />)}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-black text-white ek-section" data-testid="vs-cta-section">
        <div className="ek-container text-center max-w-3xl">
          <h2 className="ek-heading text-4xl md:text-5xl text-white">From one value stream to the whole enterprise.</h2>
          <p className="text-zinc-300 font-light text-lg mt-5">
            Each assessment feeds the same engine. When you're ready, ENTERPRISE360 classifies your
            business by archetype and assembles a tailored, function-by-function Reinvention Blueprint —
            across the value streams you choose.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mt-9">
            <Link to="/contact" className="ek-btn ek-btn-primary" data-testid="vs-final-contact">
              Book a discovery conversation <ArrowRight size={16} />
            </Link>
            <Link to="/enterprise360" className="ek-btn ek-btn-outline-light" data-testid="vs-final-engine">
              Explore ENTERPRISE360
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
